// @vue/component
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useGoalDetailsStore } from '@/stores/WheelAnalysis/goalDetails'
import { useCriticalGoalsStore } from '@/stores/WheelAnalysis/criticalGoals'
import { useChallengeStore } from '@/stores/WheelAnalysis/challenge'

const norm = v =>
    String(v ?? '')
        .trim()
        .replace(/\s+/g, '')        // remove spaces
        .replace(/\.$/, '')         // drop trailing dot (e.g., "1.1.")
        .toLowerCase()

export default {
    name: 'GoalDetails',
    setup() {
        const goalDetailsStore = useGoalDetailsStore()
        const criticalGoalsStore = useCriticalGoalsStore()
        const challengeStore = useChallengeStore()

        const { currentGoalId, currentTargetId, currentDetailType } = storeToRefs(goalDetailsStore)
        const { selectedCriticalGoals } = storeToRefs(criticalGoalsStore)
        const { editMode } = storeToRefs(challengeStore)

        const allGoalDetails = computed(() => goalDetailsStore.allGoalDetails || [])
        const rawTargets     = computed(() => goalDetailsStore.allTargetDetails || [])

        // Flatten whether it's already flat or 17 buckets
        const allTargets = computed(() =>
            Array.isArray(rawTargets.value?.[0]) ? rawTargets.value.flat(1) : rawTargets.value
        )

        const selectedGoal = computed(() =>
            allGoalDetails.value.find(g => norm(g?.id) === norm(currentGoalId.value))
        )

        const selectedTarget = computed(() => {
            const key = norm(currentTargetId.value)
            if (!key) return undefined

            for (const t of allTargets.value) {
                // match by target id/number
                if ([t?.id, t?.number].some(x => norm(x) === key)) return t
                // match by any indicator id/number → parent target
                const inds = Array.isArray(t?.indicators) ? t.indicators : []
                for (const ind of inds) {
                    if ([ind?.id, ind?.number].some(x => norm(x) === key)) return t
                }
            }

            // Helpful one-time log
            console.warn('[GoalDetails] Target not found', {
                currentGoalId: currentGoalId.value,
                currentTargetId: currentTargetId.value,
                exampleTargets: allTargets.value.slice(0, 5).map(t => t.id || t.number)
            })
            return undefined
        })

        const goalDescription   = computed(() => selectedGoal.value?.description || '')
        const targetDescription = computed(() => selectedTarget.value?.name || selectedTarget.value?.description || '')
        const indicators        = computed(() => selectedTarget.value?.indicators || [])
        const targetsLoaded     = computed(() => (allTargets.value?.length ?? 0) > 0)

        return {
            currentGoalId,
            currentTargetId,
            currentDetailType,
            goalDescription,
            targetDescription,
            indicators,
            targetsLoaded,
            selectedCriticalGoals,
            editMode
        }
    }
}
